import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { User, Briefcase, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { UserWithBuddy } from "@/lib/auth";
import { formatDateStandard } from "@servify/shared-utils";

export default function MeetBuddy() {
  const [isQuizOpen, setIsQuizOpen] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState<string[]>([]);
  const [quizSubmitted, setQuizSubmitted] = useState(false);

  // Fetch user data with buddy information
  const { data: userData, isLoading: userLoading, error: userError } = useQuery({
    queryKey: ['/auth/me-with-buddy'],
    queryFn: async () => {
      try {
        const response = await apiRequest("GET", "/auth/me-with-buddy");
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Failed to fetch buddy info:', response.status, errorText);
          throw new Error(`Failed to load buddy information: ${response.status} ${errorText}`);
        }
        return response.json();
      } catch (error: any) {
        console.error('Error fetching buddy information:', error);
        throw error;
      }
    },
    retry: 1,
  });

  // Fetch department and designation names by ID
  const { data: departmentData } = useQuery({
    queryKey: [`/public/department/${userData?.user?.buddyDepartmentId}`],
    queryFn: async () => {
      if (!userData?.user?.buddyDepartmentId) return null;
      const response = await apiRequest("GET", `/public/department/${userData.user.buddyDepartmentId}`);
      return response.json();
    },
    enabled: !!userData?.user?.buddyDepartmentId,
  });

  const { data: designationData } = useQuery({
    queryKey: [`/public/designation/${userData?.user?.buddyDesignationId}`],
    queryFn: async () => {
      if (!userData?.user?.buddyDesignationId) return null;
      const response = await apiRequest("GET", `/public/designation/${userData.user.buddyDesignationId}`);
      return response.json();
    },
    enabled: !!userData?.user?.buddyDesignationId,
  });

  const { data: officeLocationData } = useQuery({
    queryKey: [`/public/office-location/${userData?.user?.buddyOfficeLocationId}`],
    queryFn: async () => {
      if (!userData?.user?.buddyOfficeLocationId) return null;
      const response = await apiRequest("GET", `/public/office-location/${userData.user.buddyOfficeLocationId}`);
      return response.json();
    },
    enabled: !!userData?.user?.buddyOfficeLocationId,
  });

  // Fetch employees for whom current user is buddy
  const { data: buddyEmployeesData, isLoading: buddyEmployeesLoading } = useQuery({
    queryKey: ['/auth/my-buddy-employees'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/auth/my-buddy-employees");
      return response.json();
    },
  });

  // Helper functions to get buddy information
  const getBuddyName = () => {
    // If we have both first and last name, return full name
    if (userData?.user?.buddyFirstName && userData?.user?.buddyLastName) {
      return `${userData.user.buddyFirstName} ${userData.user.buddyLastName}`;
    }
    // If we have only first name, return that
    if (userData?.user?.buddyFirstName) {
      return userData.user.buddyFirstName;
    }
    // If we have only last name, return that
    if (userData?.user?.buddyLastName) {
      return userData.user.buddyLastName;
    }
    // If we have a buddy assigned but no name info, return a generic name
    if (userData?.user?.buddyUserId) {
      return "Your Buddy";
    }
    return null;
  };

  const getBuddyDepartment = () => {
    // First try to get from the API call
    if (departmentData?.name) {
      return departmentData.name;
    }
    // Fallback to the buddy's department name from the main API response
    if (userData?.user?.buddyDepartmentName) {
      return userData.user.buddyDepartmentName;
    }
    return null;
  };

  const getBuddyDesignation = () => {
    // First try to get from the API call
    if (designationData?.title) {
      return designationData.title;
    }
    // Fallback to the buddy's designation title from the main API response
    if (userData?.user?.buddyDesignationTitle) {
      return userData.user.buddyDesignationTitle;
    }
    return null;
  };

  const getBuddyWorkLocation = () => {
    // First try to get from the API call
    if (officeLocationData?.name) {
      return officeLocationData.name;
    }
    // Fallback to the buddy's office location name from the main API response
    if (userData?.user?.buddyOfficeLocationName) {
      return userData.user.buddyOfficeLocationName;
    }
    return null;
  };

  const getBuddyExperience = () => {
    if (!userData?.user?.buddyDateOfJoining) return null;
    const joinDate = new Date(userData.user.buddyDateOfJoining);
    const currentDate = new Date();
    const years = currentDate.getFullYear() - joinDate.getFullYear();
    const months = currentDate.getMonth() - joinDate.getMonth();
    const totalMonths = years * 12 + months;
    
    if (totalMonths < 12) {
      return `${totalMonths} month${totalMonths !== 1 ? 's' : ''}`;
    } else {
      return `${years} year${years !== 1 ? 's' : ''}`;
    }
  };

  const handleQuizOptionToggle = (option: string) => {
    if (quizAnswers.includes(option)) {
      setQuizAnswers(quizAnswers.filter(answer => answer !== option));
    } else {
      setQuizAnswers([...quizAnswers, option]);
    }
  };

  const handleQuizSubmit = () => {
    setQuizSubmitted(true);
  };

  const resetQuiz = () => {
    setIsQuizOpen(false);
    setQuizAnswers([]);
    setQuizSubmitted(false);
  };

  // Loading state
  if (userLoading || buddyEmployeesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-servify-purple mx-auto mb-4" />
          <p className="text-gray-600">Loading buddy information...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (userError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-2">Failed to load buddy information</p>
          {userError instanceof Error && (
            <p className="text-red-500 text-sm mb-4">{userError.message}</p>
          )}
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      </div>
    );
  }

  const user = userData?.user;
  const buddyName = getBuddyName();
  const buddyDepartment = getBuddyDepartment();
  const buddyDesignation = getBuddyDesignation();
  const buddyWorkLocation = getBuddyWorkLocation();
  const buddyExperience = getBuddyExperience();
  const buddyEmployees = buddyEmployeesData?.employees || [];

  
  // Show loading while data is being fetched
  if (userLoading || buddyEmployeesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-servify-green" />
      </div>
    );
  }
  
  // No buddy assigned
  if (!user?.buddyUserId) {
    return (
      <div className="space-y-8 p-6" data-testid="meet-buddy-page">
        <div className="space-y-2">
          <h1 className="text-4xl font-bold">
            <span className="text-servify-dark-grey">Meet Your </span>
            <span className="text-servify-purple">Buddy</span>
          </h1>
          <p className="text-servify-dark-grey/70 text-lg">
            Your work buddy will guide you through your onboarding journey at Servify.
          </p>
        </div>

        <Card className="border border-servify-light-grey shadow-sm max-w-2xl mx-auto">
          <CardContent className="p-8 text-center">
            <div className="mb-6">
              <div className="w-16 h-16 bg-servify-light-grey rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-servify-dark-grey" />
              </div>
              <h2 className="text-xl font-semibold text-servify-purple mb-2">No Buddy Assigned</h2>
              <p className="text-servify-dark-grey">
                You don't have a buddy assigned yet. Please contact your manager or HR to get a buddy assigned for your onboarding journey.
              </p>
            </div>
            <Button 
              onClick={() => window.location.href = '/feedback'}
              className="bg-servify-purple hover:bg-servify-purple/90"
            >
              Contact Support
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-6" data-testid="meet-buddy-page">
      {/* Page Title */}
      <div className="space-y-2">
        <h1 className="text-4xl font-bold">
          <span className="text-servify-dark-grey">Meet Your </span>
          <span className="text-servify-purple">Buddy</span>
        </h1>
        <p className="text-servify-dark-grey/70 text-lg">
          Your work buddy will guide you through your onboarding journey at Servify.
        </p>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
        {/* Left Section: Your Buddy */}
        <Card className="border border-servify-light-grey shadow-sm">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold text-servify-purple mb-6">Your Buddy</h2>
            

            {user?.buddyUserId ? (
              <>
                {/* Profile Picture and Basic Info */}
                <div className="flex flex-col md:flex-row gap-6 mb-6">
                  {/* Profile Picture */}
                  <div className="flex-shrink-0">
                    <div className="w-24 h-24 bg-servify-light-grey rounded-full flex items-center justify-center overflow-hidden border-4 border-servify-purple/20">
                      {user?.buddyProfilePictureUrl ? (
                        <img 
                          src={user.buddyProfilePictureUrl} 
                          alt={`${buddyName}'s profile`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="text-center">
                          <User className="w-12 h-12 text-servify-dark-grey mx-auto mb-1" />
                          <span className="text-xs text-servify-dark-grey">No Photo</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Buddy's Professional Details */}
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-servify-dark-grey mb-3">{buddyName}</h3>
                    <div className="space-y-2 text-servify-dark-grey text-sm">
                      {user?.buddyDateOfJoining && (
                        <div className="flex items-center space-x-2">
                          <svg className="w-4 h-4 text-servify-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span><strong>Experience:</strong> Part of Servify since {new Date(user.buddyDateOfJoining).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                        </div>
                      )}
                      <div className="flex items-center space-x-2">
                        <User className="w-4 h-4 text-servify-green" />
                        <span><strong>Role:</strong> {buddyDesignation || 'Not specified'}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Briefcase className="w-4 h-4 text-servify-green" />
                        <span><strong>Department:</strong> {buddyDepartment || 'Not specified'}</span>
                      </div>
                      {buddyWorkLocation && (
                        <div className="flex items-center space-x-2">
                          <svg className="w-4 h-4 text-servify-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span><strong>Location:</strong> {buddyWorkLocation}</span>
                        </div>
                      )}
                      {user?.buddyEmail && (
                        <div className="flex items-center space-x-2">
                          <svg className="w-4 h-4 text-servify-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span><strong>Email:</strong> {user.buddyEmail}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Welcome Message */}
                <div className="mb-6">
                  <p className="text-servify-dark-grey text-sm leading-relaxed mb-3">
                    We are thrilled to have you join the Servify family. We hope that you have been looking forward to this new beginning.
                  </p>
                  <p className="text-servify-dark-grey text-sm leading-relaxed">
                    We understand that you might be somewhat anxious but excited about transitioning into your new role and department. Hence, we've chosen {buddyName} as your Work Buddy, who will be your go-to-person, over the next couple of weeks.
                  </p>
                </div>

                {/* How Buddy Will Help */}
                <div>
                  <h4 className="font-semibold text-servify-purple mb-3">Here's how {buddyName} will help you</h4>
                  
                  <ul className="space-y-2 text-servify-dark-grey text-sm">
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-servify-purple rounded-full mt-2 flex-shrink-0"></span>
                      <span>Help you adapt to the work culture</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-servify-purple rounded-full mt-2 flex-shrink-0"></span>
                      <span>Guide on workflow processes</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-servify-purple rounded-full mt-2 flex-shrink-0"></span>
                      <span>First point of contact for questions</span>
                    </li>
                  </ul>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-servify-light-grey rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-servify-dark-grey" />
                </div>
                <h3 className="text-lg font-semibold text-servify-purple mb-2">No Buddy Assigned</h3>
                <p className="text-servify-dark-grey text-sm mb-4">
                  You don't have a buddy assigned yet. Please contact your manager or HR to get a buddy assigned for your onboarding journey.
                </p>
                <Button 
                  onClick={() => window.location.href = '/feedback'}
                  className="bg-servify-purple hover:bg-servify-purple/90 text-sm"
                >
                  Contact Support
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right Section: Employees You're Buddy For */}
        <Card className="border border-servify-light-grey shadow-sm">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold text-servify-purple mb-6">Employees You're Buddy For</h2>
            
            {buddyEmployees && buddyEmployees.length > 0 ? (
              <div className="space-y-4">
                {buddyEmployees.map((employee: any) => (
                  <div key={employee.id} className="flex items-center space-x-4 p-4 bg-servify-light-grey/30 rounded-lg">
                    {/* Employee Profile Picture */}
                    <div className="w-12 h-12 bg-servify-light-grey rounded-full flex items-center justify-center overflow-hidden border-2 border-servify-purple/20">
                      {employee.profilePictureUrl ? (
                        <img 
                          src={employee.profilePictureUrl} 
                          alt={`${employee.firstName} ${employee.lastName}'s profile`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-6 h-6 text-servify-dark-grey" />
                      )}
                    </div>
                    
                    {/* Employee Info */}
                    <div className="flex-1">
                      <h4 className="font-semibold text-servify-dark-grey">
                        {employee.firstName} {employee.lastName}
                      </h4>
                      <div className="text-sm text-servify-dark-grey/70 space-y-1">
                        {employee.designation && (
                          <div className="flex items-center space-x-1">
                            <User className="w-3 h-3" />
                            <span>{employee.designation}</span>
                          </div>
                        )}
                        {employee.department && (
                          <div className="flex items-center space-x-1">
                            <Briefcase className="w-3 h-3" />
                            <span>{employee.department}</span>
                          </div>
                        )}
                        {employee.dateOfJoining && (
                          <div className="flex items-center space-x-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span>Joined: {new Date(employee.dateOfJoining).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-servify-light-grey rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-servify-dark-grey" />
                </div>
                <h3 className="text-lg font-semibold text-servify-purple mb-2">No Employees Assigned</h3>
                <p className="text-servify-dark-grey text-sm">
                  You are not currently assigned as a buddy for any employees.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

    </div>
  );
}
